'use client';

import React, { useState, useEffect, useRef } from "react";
import { useParams, useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import { ChevronRight, ChevronLeft, Filter, X } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { ProductCard } from "@/components/product/product-card";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { Category } from "@/lib/types";
import { useAuth } from "@/contexts/auth-context";
import type { InitialFeed } from "@/lib/listing-seo";
import { pageFromParam } from "@/lib/page-param";

function hasActiveFilters(f: { priceRange: string[]; variantOptions: Record<string, string[]>; productProperties: Record<string, string[]>; productTypes: string[] }, price: [number, number]) {
  return f.priceRange.length > 0 || f.productTypes.length > 0 || price[0] > 0 || price[1] < 20000
    || Object.values(f.variantOptions).some((v) => v.length > 0) || Object.values(f.productProperties).some((v) => v.length > 0)
}

export function CategoryBrowser({ initialFeed, intro }: { initialFeed: InitialFeed | null; intro: string }) {
  const params = useParams();
  const router = useRouter();
  const searchParams = useSearchParams();
  const handle = params.handle as string;
  const { customer } = useAuth();

  const [currentPage, setCurrentPage] = useState(initialFeed?.page ?? 1);
  const [sortBy, setSortBy] = useState<string>(initialFeed?.sort ?? '');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const [showAllOptions, setShowAllOptions] = useState<Record<string, boolean>>({});
  const [imageBg, setImageBg] = useState<'white' | 'black'>('white');
  const [priceSliderRange, setPriceSliderRange] = useState<[number, number]>([0, 20000]);
  const [isInitialized, setIsInitialized] = useState(false);

  const itemsPerPage = 24;

  const [dynamicFilters, setDynamicFilters] = useState({
    priceRange: [] as string[],
    variantOptions: {} as Record<string, string[]>,
    productProperties: {} as Record<string, string[]>,
    productTypes: [] as string[]
  });

  const [tempFilters, setTempFilters] = useState({
    priceRange: [] as string[],
    variantOptions: {} as Record<string, string[]>,
    productProperties: {} as Record<string, string[]>,
    productTypes: [] as string[]
  });

  const [tempPriceSliderRange, setTempPriceSliderRange] = useState<[number, number]>([0, 20000]);

  // Initialize filters from URL on mount
  useEffect(() => {
    if (isInitialized) return;

    const urlFilters = {
      priceRange: [] as string[],
      variantOptions: {} as Record<string, string[]>,
      productProperties: {} as Record<string, string[]>,
      productTypes: [] as string[]
    };

    // Parse variant options from URL
    searchParams.forEach((value, key) => {
      if (key.startsWith('vo_')) {
        const optionName = key.replace('vo_', '').replace(/_/g, ' ');
        urlFilters.variantOptions[optionName] = Array.from(new Set(value.split(',')));
      } else if (key.startsWith('pp_')) {
        const propName = key.replace('pp_', '').replace(/_/g, ' ');
        urlFilters.productProperties[propName] = Array.from(new Set(value.split(',')));
      }
    });

    const urlSort = searchParams.get('sort');
    const urlPage = searchParams.get('page');
    const urlMinPrice = searchParams.get('minPrice');
    const urlMaxPrice = searchParams.get('maxPrice');

    if (urlSort) setSortBy(urlSort);
    if (urlPage) setCurrentPage(pageFromParam(urlPage));
    if (urlMinPrice || urlMaxPrice) {
      setPriceSliderRange([
        urlMinPrice ? parseInt(urlMinPrice) : 0,
        urlMaxPrice ? parseInt(urlMaxPrice) : 20000
      ]);
    }

    if (Object.keys(urlFilters.variantOptions).length > 0 || Object.keys(urlFilters.productProperties).length > 0) {
      setDynamicFilters(urlFilters);
    }

    setIsInitialized(true);
  }, [searchParams, isInitialized]);

  // Update URL when filters change
  useEffect(() => {
    if (!isInitialized) return;

    const params = new URLSearchParams();

    // Add sort
    if (sortBy) params.set('sort', sortBy);

    // Add page
    if (currentPage > 1) params.set('page', currentPage.toString());

    // Add price range
    if (priceSliderRange[0] > 0) params.set('minPrice', priceSliderRange[0].toString());
    if (priceSliderRange[1] < 20000) params.set('maxPrice', priceSliderRange[1].toString());

    // Add variant options
    Object.entries(dynamicFilters.variantOptions).forEach(([key, values]) => {
      if (values.length > 0) {
        params.set(`vo_${key.replace(/\s+/g, '_')}`, values.join(','));
      }
    });

    // Add product properties
    Object.entries(dynamicFilters.productProperties).forEach(([key, values]) => {
      if (values.length > 0) {
        params.set(`pp_${key.replace(/\s+/g, '_')}`, values.join(','));
      }
    });

    const queryString = params.toString();
    const newUrl = queryString ? `/${handle}?${queryString}` : `/${handle}`;
    router.replace(newUrl, { scroll: false });
  }, [sortBy, currentPage, priceSliderRange, dynamicFilters, handle, router, isInitialized]);

  // Reset to page 1 when the user changes filters or sort. The first value seen after the URL has been
  // read is the baseline, so loading /handle?page=3 keeps page 3 instead of resetting it.
  const filterKey = JSON.stringify([sortBy, dynamicFilters, priceSliderRange]);
  const lastFilterKey = useRef<string | null>(null);
  useEffect(() => {
    if (!isInitialized) return;
    if (lastFilterKey.current !== null && lastFilterKey.current !== filterKey) setCurrentPage(1);
    lastFilterKey.current = filterKey;
  }, [filterKey, isInitialized]);

  // Fetch single category by handle - fast, unblocks product loading
  const { data: category, isLoading: isCategoryLoading, isError: isCategoryError } = useQuery<Category>({
    queryKey: ["/api/categories", handle],
    queryFn: async () => {
      const response = await fetch(`/api/categories/${handle}`);
      if (!response.ok) {
        throw new Error('Category not found');
      }
      return response.json();
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: false, // Don't retry on 404 errors
    initialData: initialFeed?.category,
  });

  // Fetch all categories in background for breadcrumb navigation
  const { data: categories = [] } = useQuery<Category[]>({
    queryKey: ["/api/categories"],
    queryFn: async () => {
      const response = await fetch(`/api/categories`);
      if (!response.ok) return [];
      return response.json();
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  // Fetch variant options from database - cached per category
  const { data: variantOptions } = useQuery({
    queryKey: ["/api/variant-options", category?.id],
    queryFn: async () => {
      if (!category?.id) return { options: {} };
      const response = await fetch(`/api/variant-options?category=${category.id}`);
      return response.json();
    },
    enabled: !!category?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });

  // Fetch variants with pagination
  const { data: productData, isLoading, isFetching } = useQuery({
    queryKey: ["/api/variants", category?.id, currentPage, sortBy, dynamicFilters, priceSliderRange],
    queryFn: async () => {
      if (!category?.id) return { products: [], total: 0 };

      const offset = (currentPage - 1) * itemsPerPage;
      const limit = itemsPerPage;

      // Map frontend sort values to backend API values
      let apiSortBy = 'newest';
      if (sortBy) {
        if (sortBy === 'price-low-high') {
          apiSortBy = 'price_asc';
        } else if (sortBy === 'price-high-low') {
          apiSortBy = 'price_desc';
        }
      }

      const params = new URLSearchParams({
        category: category.id,
        limit: String(limit),
        offset: String(offset),
        sortBy: apiSortBy
      });

      // Add price range filters from checkboxes if any
      if (dynamicFilters.priceRange.length > 0) {
        dynamicFilters.priceRange.forEach(range => {
          if (range === 'under-500') params.append('maxPrice', '500');
          else if (range === '500-1000') {
            params.append('minPrice', '500');
            params.append('maxPrice', '1000');
          } else if (range === '1000-5000') {
            params.append('minPrice', '1000');
            params.append('maxPrice', '5000');
          } else if (range === '5000-10000') {
            params.append('minPrice', '5000');
            params.append('maxPrice', '10000');
          } else if (range === 'above-10000') {
            params.append('minPrice', '10000');
          }
        });
      }

      // Add price slider range
      if (priceSliderRange[0] > 0) {
        params.append('minPrice', String(priceSliderRange[0]));
      }
      if (priceSliderRange[1] < 20000) {
        params.append('maxPrice', String(priceSliderRange[1]));
      }

      if (dynamicFilters.productTypes.length > 0) {
        dynamicFilters.productTypes.forEach(type => params.append('productType', type));
      }

      // Add variant options filters
      Object.entries(dynamicFilters.variantOptions).forEach(([optionName, values]) => {
        if (values.length > 0) {
          values.forEach(value => params.append(`variantOption_${optionName}`, value));
        }
      });

      // Add product properties filters
      Object.entries(dynamicFilters.productProperties).forEach(([propName, values]) => {
        if (values.length > 0) {
          values.forEach(value => params.append(`productProperty_${propName}`, value));
        }
      });

      const response = await fetch(`/api/variants?${params.toString()}`);
      const data = await response.json();
      return { products: data.products || [], total: data.total || 0 };
    },
    enabled: !!category?.id,
    staleTime: 60000,
    gcTime: 300000,
    placeholderData: keepPreviousData, // Keep showing previous data while new data loads
    // The server rendered this exact page (no filters, same page and sort), so the grid is in the HTML
    // and the first client render matches it. Any other query starts empty and fetches as before.
    // An empty server feed is not seeded: it may be a failed backend call, so the browser retries it.
    initialData:
      initialFeed && initialFeed.products.length > 0 && category?.id && initialFeed.page === currentPage && initialFeed.sort === sortBy && !hasActiveFilters(dynamicFilters, priceSliderRange)
        ? { products: initialFeed.products, total: initialFeed.total }
        : undefined,
  });

  // Let each ProductCard fetch its own tax rate based on product type
  // via getTaxRateForProduct (cached client-side) instead of hardcoding 18%

  const products = productData?.products || [];
  const totalProducts = productData?.total ?? 0;

  // Products are already filtered and paginated by the server
  const displayProducts = products;
  const totalPages = Math.ceil(totalProducts / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalProducts);

  // Real hrefs for crawlers and no-JS visitors; clicks are handled in place by handlePageChange.
  const pageHref = (n: number) => {
    const p = new URLSearchParams(searchParams.toString());
    if (n > 1) p.set('page', String(n)); else p.delete('page');
    const qs = p.toString();
    return qs ? `/${handle}?${qs}` : `/${handle}`;
  };

  // Plain left clicks page in place; modifier and middle clicks keep the browser's open-in-new-tab.
  const onPageLinkClick = (page: number) => (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    e.preventDefault();
    handlePageChange(page);
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const clearAllFilters = () => {
    setDynamicFilters({
      priceRange: [],
      variantOptions: {},
      productProperties: {},
      productTypes: []
    });
    setPriceSliderRange([0, 20000]);
    setCurrentPage(1);
  };

  const applyMobileFilters = () => {
    setDynamicFilters(tempFilters);
    setPriceSliderRange(tempPriceSliderRange);
    setIsFilterOpen(false);
  };

  const cancelMobileFilters = () => {
    setTempFilters({ ...dynamicFilters });
    setTempPriceSliderRange([...priceSliderRange] as [number, number]);
    setIsFilterOpen(false);
  };

  // Show skeleton while category is loading - matches actual UI layout
  if (isCategoryLoading) {
    return (
      <div className="min-h-screen hv-page bg-hv-paper font-lexend text-hv-ink">
        {/* Breadcrumb skeleton - desktop */}
        <div className="hidden xl:block border-b border-hv-line bg-hv-paper">
          <div className="w-full xl:ml-72 px-6 py-2">
            <div className="flex items-center space-x-3">
              <div className="h-4 w-12 bg-hv-card rounded animate-pulse" />
              <ChevronRight className="w-4 h-4 text-hv-line" />
              <div className="h-4 w-24 bg-hv-card rounded animate-pulse" />
              <ChevronRight className="w-4 h-4 text-hv-line" />
              <div className="h-4 w-32 bg-hv-card rounded animate-pulse" />
            </div>
          </div>
        </div>

        {/* Mobile controls skeleton */}
        <div className="xl:hidden border-b border-hv-line bg-hv-paper w-full">
          <div className="w-full px-2.5 py-3">
            <div className="flex items-center justify-between gap-3 h-10">
              <div className="h-10 w-20 bg-hv-card rounded-full animate-pulse" />
              <div className="flex items-center gap-2">
                <div className="h-10 w-10 bg-hv-card rounded-full animate-pulse" />
                <div className="h-6 w-16 bg-hv-card rounded-full animate-pulse" />
                <div className="h-10 w-20 bg-hv-card rounded-full animate-pulse" />
              </div>
            </div>
          </div>
        </div>

        <section className="pb-3 xl:py-6 min-h-screen">
          <div className="w-full max-w-[1920px] mx-auto px-0 xl:px-6">
            <div className="flex flex-col xl:flex-row gap-0 xl:gap-6">
              {/* Sidebar skeleton - desktop */}
              <aside className="hidden xl:block xl:w-72 xl:flex-shrink-0">
                <div className="overflow-hidden rounded-2xl border border-hv-line bg-white sticky top-[120px]">
                  <div className="p-5 border-b border-hv-line">
                    <div className="flex items-center gap-2">
                      <div className="h-5 w-5 bg-hv-card rounded animate-pulse" />
                      <div className="h-5 w-16 bg-hv-card rounded animate-pulse" />
                    </div>
                  </div>
                  <div className="max-h-[calc(100vh-200px)] overflow-y-auto p-5 custom-scrollbar">
                    <div className="space-y-4">
                      <div className="border-b border-hv-line pb-4">
                        <div className="h-5 w-16 bg-hv-card rounded animate-pulse mb-3" />
                        <div className="h-12 bg-hv-card rounded animate-pulse" />
                      </div>
                      {[...Array(3)].map((_, i) => (
                        <div key={i} className="border-b border-hv-line pb-4">
                          <div className="h-5 w-32 bg-hv-card rounded animate-pulse mb-2" />
                          <div className="space-y-2">
                            {[...Array(3)].map((_, j) => (
                              <div key={j} className="flex items-center gap-2">
                                <div className="h-4 w-4 bg-hv-card rounded animate-pulse" />
                                <div className="h-4 w-24 bg-hv-card rounded animate-pulse" />
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </aside>

              {/* Product grid skeleton */}
              <div className="flex-1">
                {/* Desktop sort skeleton */}
                <div className="hidden xl:flex items-center justify-between mb-4 px-0 pt-0">
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-40 bg-hv-card rounded-full animate-pulse" />
                    <div className="h-6 w-20 bg-hv-card rounded-full animate-pulse" />
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="h-5 w-16 bg-hv-card rounded animate-pulse" />
                    <div className="h-9 w-48 bg-hv-card rounded-full animate-pulse" />
                  </div>
                </div>

                <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 md:gap-x-5 md:gap-y-8 xl:gap-x-8">
                  {[...Array(24)].map((_, i) => (
                    <div key={i} className="flex flex-col gap-2.5 bg-white pb-3 border-b border-r border-hv-line even:border-r-0 md:border md:even:border-r">
                      <div className="h-[200px] bg-hv-card animate-pulse md:h-[230px]" />
                      <div className="space-y-2 px-3">
                        <div className="h-4 w-3/4 bg-hv-card rounded animate-pulse" />
                        <div className="h-3 w-1/2 bg-hv-card rounded animate-pulse" />
                        <div className="h-5 w-20 bg-hv-card rounded animate-pulse" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  // Show error state if category fetch failed
  if (isCategoryError || (!isCategoryLoading && !category)) {
    return (
      <div className="min-h-[calc(100vh-180px)] xl:min-h-[calc(100vh-120px)] hv-page bg-hv-paper font-lexend text-hv-ink flex items-center justify-center px-4 py-12">
        <div className="max-w-md w-full">
          <div className="rounded-2xl border border-hv-line bg-white p-6 xl:p-8 text-center">
            {/* Icon */}
            <div className="w-16 h-16 xl:w-20 xl:h-20 bg-hv-card rounded-full flex items-center justify-center mx-auto mb-4 xl:mb-6">
              <svg className="w-8 h-8 xl:w-10 xl:h-10 text-hv-orange" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>

            {/* Title */}
            <h1 className="text-2xl xl:text-3xl font-semibold text-hv-ink mb-2 xl:mb-3 tracking-[-0.02em]">
              Category not found
            </h1>

            {/* Description */}
            <p className="text-sm xl:text-base text-hv-soft mb-2">
              The category <span className="font-semibold text-hv-ink">&quot;{handle}&quot;</span> does not exist.
            </p>
            <p className="text-xs xl:text-sm text-hv-soft mb-6 xl:mb-8">
              It may have been removed or the URL might be incorrect.
            </p>

            {/* Action Button */}
            <Link href="/">
              <Button className="w-full xl:w-auto h-12 bg-hv-ink hover:bg-black text-white px-6 xl:px-8 rounded-xl font-semibold text-sm xl:text-base shadow-none">
                <svg className="w-4 h-4 xl:w-5 xl:h-5 mr-2 inline-block" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
                Go back home
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }
  const getBreadcrumbPath = (currentCat: Category | undefined): Category[] => {
    const path: Category[] = [];
    let current = currentCat;
    const visited = new Set(); // Prevent infinite loops
    while (current && !visited.has(current.id)) {
      visited.add(current.id);
      path.unshift(current);
      const parentId = current.parentId;
      current = categories.find(c => c.id === parentId);
    }
    return path;
  };

  const breadcrumbPath = getBreadcrumbPath(category);

  // Remove any intermediary "Shop by Category" nodes and then show only Home + grandparent + current
  const cleanedPath = (breadcrumbPath || []).filter(cat => {
    const name = (cat?.name || '').toLowerCase();
    const handle = (cat?.handle || '').toLowerCase();
    return name !== 'shop by category' && handle !== 'shop-by-category';
  });

  // Show grandparent (two levels up) and current when there are 3+ levels.
  // If there are exactly 2 levels, keep both parent + current. If only 1, show current only.
  let displayBreadcrumbPath: typeof cleanedPath = [];
  if (cleanedPath.length >= 3) {
    const grandparent = cleanedPath[cleanedPath.length - 3];
    const current = cleanedPath[cleanedPath.length - 1];
    displayBreadcrumbPath = [grandparent, current];
  } else if (cleanedPath.length === 2) {
    // Keep both parent and current (no removal)
    displayBreadcrumbPath = [cleanedPath[0], cleanedPath[1]];
  } else if (cleanedPath.length === 1) {
    displayBreadcrumbPath = [cleanedPath[0]];
  } else {
    displayBreadcrumbPath = [];
  }

  return (
    <div className="hv-page min-h-screen bg-hv-paper font-lexend text-hv-ink">
      {/* Breadcrumb + Product Count + Change Background */}
      <div className="hidden xl:block border-b border-hv-line bg-hv-paper">
        <div className="w-full max-w-[1920px] mx-auto px-6 py-3">
          <nav className="flex items-center justify-between text-sm">
            <div className="flex items-center space-x-3">
              <Link href="/" className="text-hv-soft transition-colors hover:text-hv-orange">
                Home
              </Link>
              <ChevronRight className="w-4 h-4 text-hv-soft/60" />
              {categories.length === 0 ? (
                <div className="flex items-center space-x-2 animate-pulse">
                  <div className="h-4 w-12 bg-hv-card rounded" />
                  <ChevronRight className="w-4 h-4 text-hv-line" />
                  <div className="h-4 w-32 bg-hv-card rounded" />
                </div>
              ) : (
                <>
                  {displayBreadcrumbPath.map((pathCat, index) => {
                    const isLast = index === displayBreadcrumbPath.length - 1;
                    return (
                      <React.Fragment key={pathCat.id}>
                        {isLast ? (
                          <span className="font-semibold text-hv-ink truncate max-w-[200px]">
                            {pathCat.name}
                          </span>
                        ) : (
                          <>
                            <span
                              className="text-hv-soft whitespace-nowrap"
                            >
                              {pathCat.name}
                            </span>
                            <ChevronRight className="w-4 h-4 text-hv-soft/60 flex-shrink-0" />
                          </>
                        )}
                      </React.Fragment>
                    );
                  })}
                  {breadcrumbPath.length === 0 && (
                    <span className="font-semibold text-hv-ink">
                      {handle.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                    </span>
                  )}
                </>
              )}
              <div className="h-4 w-px bg-hv-line mx-2" />
              <div className="whitespace-nowrap rounded-full bg-hv-card px-3 py-1 font-hv-mono text-[11px] uppercase tracking-[0.04em] text-hv-soft">
                {totalProducts} Product{totalProducts !== 1 ? 's' : ''}
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setImageBg(imageBg === 'white' ? 'black' : 'white')}
                className="h-9 rounded-full border-hv-line bg-white px-4 shadow-none transition-colors hover:border-hv-ink hover:bg-white flex items-center gap-2"
                title={`Switch to ${imageBg === 'white' ? 'black' : 'white'} background`}
              >
                <div className={`w-4 h-4 rounded-full border-2 transition-colors ${imageBg === 'white' ? 'bg-white border-hv-line' : 'bg-black border-hv-ink'
                  }`} />
                <span className="font-hv-mono text-[11px] uppercase tracking-[0.04em] text-hv-ink">Change background</span>
              </Button>
            </div>
          </nav>
        </div>
      </div>

      {/* Mobile Controls */}
      <div className="xl:hidden border-b border-hv-line bg-hv-paper w-full">
        <div className="w-full px-3 py-2.5">
          <div className="flex items-center justify-between gap-2 h-8">
            <div className="flex items-center gap-2">
              {/* Mobile Sort Button */}
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="outline" className="h-full rounded-full border-hv-line bg-white px-3.5 shadow-none transition-colors hover:border-hv-ink hover:bg-white flex items-center justify-center group">
                    <svg className="w-3.5 h-3.5 mr-1 text-hv-ink" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
                    </svg>
                    <span className="font-hv-mono text-[11px] uppercase tracking-[0.04em] text-hv-ink">Sort</span>
                  </Button>
                </SheetTrigger>
                <SheetContent side="bottom" className="hv-page h-auto rounded-t-2xl bg-hv-paper font-lexend text-hv-ink">
                  <SheetHeader>
                    <SheetTitle className="text-xl font-semibold text-hv-ink">Sort by price</SheetTitle>
                  </SheetHeader>
                  <div className="mt-6 space-y-3 pb-6">
                    {[
                      { value: 'price-low-high', label: 'Price: Low to High', icon: '↑' },
                      { value: 'price-high-low', label: 'Price: High to Low', icon: '↓' }
                    ].map(option => (
                      <label
                        key={option.value}
                        className={`flex items-center gap-3 p-4 rounded-xl cursor-pointer border-2 transition-all duration-200 ${sortBy === option.value
                          ? 'bg-white border-hv-ink'
                          : 'bg-white border-hv-line hover:border-hv-ink'
                          }`}
                      >
                        <input
                          type="radio"
                          name="sort"
                          checked={sortBy === option.value}
                          onChange={() => setSortBy(option.value)}
                          className="w-5 h-5 accent-hv-ink cursor-pointer"
                        />
                        <div className="flex items-center gap-2 flex-1">
                          <span className="text-2xl">{option.icon}</span>
                          <span className={`text-base font-medium ${sortBy === option.value ? 'text-hv-ink' : 'text-hv-soft'
                            }`}>{option.label}</span>
                        </div>
                      </label>
                    ))}
                    {sortBy && (
                      <button
                        onClick={() => setSortBy('')}
                        className="w-full mt-2 text-sm font-medium text-hv-orange py-2 hover:bg-hv-card rounded-xl transition-colors"
                      >
                        Clear Sort
                      </button>
                    )}
                  </div>
                </SheetContent>
              </Sheet>

              <Button
                variant="outline"
                onClick={() => setImageBg(imageBg === 'white' ? 'black' : 'white')}
                className="h-full rounded-full border-hv-line bg-white px-3 shadow-none transition-colors hover:border-hv-ink hover:bg-white flex items-center justify-center group"
                title={`Switch to ${imageBg === 'white' ? 'black' : 'white'} background`}
              >
                <div className={`w-4 h-4 rounded-full border-2 transition-colors ${imageBg === 'white' ? 'bg-white border-hv-line' : 'bg-black border-hv-ink'
                  }`} />
              </Button>

              <div className="whitespace-nowrap rounded-full bg-hv-card px-2.5 py-1 font-hv-mono text-[10px] uppercase tracking-[0.04em] text-hv-soft">
                {totalProducts} Product{totalProducts !== 1 ? 's' : ''}
              </div>
            </div>

            <Sheet open={isFilterOpen} onOpenChange={(open) => {
              if (open) {
                setTempFilters({ ...dynamicFilters });
                setTempPriceSliderRange([...priceSliderRange] as [number, number]);
              }
              setIsFilterOpen(open);
            }}>
              <SheetTrigger asChild>
                <Button variant="outline" className="h-full rounded-full border-hv-line bg-white px-3.5 shadow-none transition-colors hover:border-hv-ink hover:bg-white flex items-center justify-center gap-1.5 group">
                  <Filter className="w-3.5 h-3.5 text-hv-ink" />
                  <span className="font-hv-mono text-[11px] uppercase tracking-[0.04em] text-hv-ink">Filters</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="bottom" className="hv-page h-[90vh] rounded-t-2xl bg-hv-paper font-lexend text-hv-ink flex flex-col">
                <SheetHeader className="flex-shrink-0">
                  <SheetTitle className="text-xl font-semibold text-hv-ink">Filters</SheetTitle>
                </SheetHeader>
                <div className="flex-1 overflow-y-auto mt-6 pb-24 -webkit-overflow-scrolling-touch touch-pan-y scroll-smooth" style={{ maxHeight: 'calc(90vh - 120px)' }}>
                  <FilterContent
                    isMobile={true}
                    variantOptions={variantOptions}
                    dynamicFilters={dynamicFilters}
                    setDynamicFilters={setDynamicFilters}
                    tempFilters={tempFilters}
                    setTempFilters={setTempFilters}
                    expandedSections={expandedSections}
                    setExpandedSections={setExpandedSections}
                    showAllOptions={showAllOptions}
                    setShowAllOptions={setShowAllOptions}
                    priceSliderRange={tempPriceSliderRange}
                    setPriceSliderRange={setTempPriceSliderRange}
                  />
                </div>
                <div className="flex-shrink-0 absolute bottom-0 left-0 right-0 bg-hv-paper p-4 border-t border-hv-line">
                  <div className="flex gap-3 max-w-md mx-auto">
                    <Button variant="outline" className="h-11 flex-1 rounded-xl border-[1.5px] border-hv-ink bg-transparent font-semibold text-hv-ink hover:bg-hv-ink hover:text-white" onClick={cancelMobileFilters}>
                      Cancel
                    </Button>
                    <Button className="h-11 flex-1 rounded-xl bg-hv-yellow font-semibold text-hv-ink shadow-none hover:bg-hv-yellow hover:brightness-95" onClick={applyMobileFilters}>
                      Apply filters
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>

      {/* Products Section */}
      <section className="pb-3 xl:py-6 min-h-screen">
        <div className="w-full max-w-[1920px] mx-auto px-0 xl:px-6">
          <div className="flex flex-col xl:flex-row gap-0 xl:gap-6">
            {/* Desktop Filters - Static Left Sidebar */}
            <aside className="hidden xl:block xl:w-72 xl:flex-shrink-0">
              <div className="sticky top-[120px] overflow-hidden rounded-2xl border border-hv-line bg-white">
                <div className="px-5 py-4 border-b border-hv-line">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-hv-orange" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                      </svg>
                      <h3 className="font-hv-mono text-[12px] uppercase tracking-[0.06em] text-hv-ink">Filters</h3>
                    </div>

                  </div>
                </div>
                <div className="max-h-[calc(100vh-200px)] overflow-y-auto p-5 custom-scrollbar">
                  <FilterContent
                    variantOptions={variantOptions}
                    dynamicFilters={dynamicFilters}
                    setDynamicFilters={setDynamicFilters}
                    tempFilters={tempFilters}
                    setTempFilters={setTempFilters}
                    expandedSections={expandedSections}
                    setExpandedSections={setExpandedSections}
                    showAllOptions={showAllOptions}
                    setShowAllOptions={setShowAllOptions}
                    priceSliderRange={priceSliderRange}
                    setPriceSliderRange={setPriceSliderRange}
                  />
                </div>
              </div>
            </aside>

            {/* Products Grid */}
            <div className="flex-1 min-w-0">
              <h1 className="px-3 pt-4 pb-2 xl:px-0 xl:pt-0 xl:pb-4 text-2xl xl:text-3xl font-semibold tracking-[-0.02em] text-hv-ink">
                {category?.name}
              </h1>
              {intro && <p className="px-3 pb-3 xl:px-0 xl:pb-4 -mt-1 xl:-mt-2 max-w-3xl text-sm xl:text-[15px] leading-relaxed text-hv-soft">{intro}</p>}
              {/* Desktop Sort Section + Active Filter Chips */}
              {/* Desktop Sort Section + Active Filter Chips */}
              <div className="hidden xl:block mb-4 px-0 pt-0">
                <div className="flex items-center justify-between">
                  {/* Active Filter Chips */}
                  <div className="flex items-center gap-2 flex-wrap flex-1 min-w-0 mr-4">
                    {(() => {
                      const activeChips: { label: string; filterType: 'variantOptions' | 'productProperties'; optionName: string; value: string }[] = [];
                      const seenValues = new Set<string>();

                      Object.entries(dynamicFilters.variantOptions).forEach(([optionName, values]) => {
                        (values as string[]).forEach(value => {
                          if (!seenValues.has(value)) {
                            seenValues.add(value);
                            activeChips.push({ label: `${value}`, filterType: 'variantOptions', optionName, value });
                          }
                        });
                      });
                      Object.entries(dynamicFilters.productProperties).forEach(([optionName, values]) => {
                        (values as string[]).forEach(value => {
                          if (!seenValues.has(value)) {
                            seenValues.add(value);
                            activeChips.push({ label: `${value}`, filterType: 'productProperties', optionName, value });
                          }
                        });
                      });
                      if (priceSliderRange[0] > 0 || priceSliderRange[1] < 20000) {
                        activeChips.push({ label: `₹${priceSliderRange[0].toLocaleString('en-IN')} - ₹${priceSliderRange[1].toLocaleString('en-IN')}`, filterType: 'variantOptions', optionName: '__price__', value: '__price__' });
                      }
                      if (activeChips.length === 0) return null;
                      return (
                        <>
                          {activeChips.map((chip, idx) => (
                            <span
                              key={`${chip.filterType}-${chip.optionName}-${chip.value}-${idx}`}
                              className="inline-flex items-center gap-1.5 rounded-full border border-hv-line bg-white px-3 py-1.5 text-xs font-medium text-hv-ink transition-colors hover:border-hv-ink"
                            >
                              {chip.label}
                              <button
                                onClick={() => {
                                  if (chip.optionName === '__price__') {
                                    setPriceSliderRange([0, 20000]);
                                  } else {
                                    setDynamicFilters((prev: typeof dynamicFilters) => {
                                      const newFilters = { ...prev };
                                      const type = chip.filterType;
                                      newFilters[type] = { ...newFilters[type] };
                                      newFilters[type][chip.optionName] = (newFilters[type][chip.optionName] || []).filter((v: string) => v !== chip.value);
                                      if (newFilters[type][chip.optionName].length === 0) {
                                        delete newFilters[type][chip.optionName];
                                      }
                                      return newFilters;
                                    });
                                  }
                                }}
                                className="rounded-full p-0.5 text-hv-soft transition-colors hover:bg-hv-card hover:text-hv-ink"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          ))}
                          <button
                            onClick={clearAllFilters}
                            className="rounded-lg px-2 py-1 text-xs font-semibold text-hv-orange transition-colors hover:bg-hv-card"
                          >
                            Clear All
                          </button>
                        </>
                      );
                    })()}
                  </div>
                  {/* Sort Controls */}
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className="font-hv-mono text-[11px] uppercase tracking-[0.06em] text-hv-soft">Sort by</span>
                    <Select value={sortBy} onValueChange={setSortBy}>
                      <SelectTrigger className="h-10 w-[220px] rounded-full border-hv-line bg-white px-4 text-hv-ink shadow-none transition-colors hover:border-hv-ink focus:ring-hv-yellow/40">
                        <div className="flex items-center gap-2">
                          <svg className="w-4 h-4 text-hv-soft" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
                          </svg>
                          <SelectValue placeholder="Price order" />
                        </div>
                      </SelectTrigger>
                      <SelectContent className="rounded-xl border-hv-line bg-white font-lexend text-hv-ink shadow-lg">
                        <SelectItem
                          value="price-low-high"
                          className="cursor-pointer rounded-lg hover:bg-hv-paper focus:bg-hv-paper focus:text-hv-ink"
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-lg">↑</span>
                            <span>Price: Low to High</span>
                          </div>
                        </SelectItem>
                        <SelectItem
                          value="price-high-low"
                          className="cursor-pointer rounded-lg hover:bg-hv-paper focus:bg-hv-paper focus:text-hv-ink"
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-lg">↓</span>
                            <span>Price: High to Low</span>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    {sortBy && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSortBy('')}
                        className="h-9 rounded-full text-hv-orange hover:bg-hv-card hover:text-hv-orange"
                      >
                        Clear
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {/* Show skeleton on initial load with no data */}
              {isLoading && displayProducts.length === 0 ? (
                <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 md:gap-x-5 md:gap-y-8 xl:gap-x-8">
                  {[...Array(24)].map((_, i) => (
                    <div key={i} className="flex flex-col gap-2.5 bg-white pb-3 border-b border-r border-hv-line even:border-r-0 md:border md:even:border-r">
                      <div className="h-[200px] bg-hv-card animate-pulse md:h-[230px]" />
                      <div className="space-y-2 px-3">
                        <div className="h-3 w-1/3 bg-hv-card rounded animate-pulse" />
                        <div className="h-4 w-3/4 bg-hv-card rounded animate-pulse" />
                        <div className="h-5 w-24 bg-hv-card rounded animate-pulse" />
                        <div className="h-9 w-full bg-hv-card rounded-full animate-pulse" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <>
                  {/* Subtle refresh indicator when updating data in background */}
                  {isFetching && displayProducts.length > 0 && (
                    <div className="flex items-center justify-center mb-4">
                      <div className="inline-flex items-center gap-2 px-4 py-2 bg-hv-card text-hv-soft rounded-full text-sm">
                        <div className="w-4 h-4 border-2 border-hv-line border-t-hv-orange rounded-full animate-spin"></div>
                        <span>Updating...</span>
                      </div>
                    </div>
                  )}
                  <div className={`grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 md:gap-x-5 md:gap-y-8 xl:gap-x-8 ${isFetching ? 'opacity-70 transition-opacity' : ''}`}>
                    {displayProducts.map((product: any, index: number) => (
                      <ProductCard
                        key={product.variants?.[0]?.id || product.id}
                        product={product}
                        imageBg={imageBg}
                        priority={index < 4}
                      />
                    ))}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="mt-8 flex justify-center items-center gap-2 px-0">
                      {currentPage > 1 ? (
                        <Link href={pageHref(currentPage - 1)} rel="prev" aria-label="Previous page" onClick={onPageLinkClick(currentPage - 1)}
                          className="flex h-10 w-10 items-center justify-center rounded-full border border-hv-line bg-white text-hv-ink hover:border-hv-ink">
                          <ChevronLeft className="h-4 w-4" />
                        </Link>
                      ) : (
                        <span aria-disabled="true" className="flex h-10 w-10 items-center justify-center rounded-full border border-hv-line bg-white text-hv-soft"><ChevronLeft className="h-4 w-4" /></span>
                      )}

                      <div className="flex items-center gap-1">
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                          const showPage =
                            page === 1 ||
                            page === totalPages ||
                            (page >= currentPage - 1 && page <= currentPage + 1);

                          const showEllipsis =
                            (page === currentPage - 2 && currentPage > 3) ||
                            (page === currentPage + 2 && currentPage < totalPages - 2);

                          if (showEllipsis) {
                            return <span key={page} className="px-2 text-hv-soft">…</span>;
                          }

                          if (!showPage) return null;

                          return (
                            <Link key={page} href={pageHref(page)} aria-current={currentPage === page ? 'page' : undefined}
                              onClick={onPageLinkClick(page)}
                              className={`flex h-10 w-10 items-center justify-center rounded-full border text-sm font-medium transition-colors ${currentPage === page ? "bg-hv-ink text-white border-hv-ink" : "border-hv-line bg-white text-hv-ink hover:border-hv-ink"}`}>
                              {page}
                            </Link>
                          );
                        })}
                      </div>

                      {currentPage < totalPages ? (
                        <Link href={pageHref(currentPage + 1)} rel="next" aria-label="Next page" onClick={onPageLinkClick(currentPage + 1)}
                          className="flex h-10 w-10 items-center justify-center rounded-full border border-hv-line bg-white text-hv-ink hover:border-hv-ink">
                          <ChevronRight className="h-4 w-4" />
                        </Link>
                      ) : (
                        <span aria-disabled="true" className="flex h-10 w-10 items-center justify-center rounded-full border border-hv-line bg-white text-hv-soft"><ChevronRight className="h-4 w-4" /></span>
                      )}
                    </div>
                  )}

                  {/* Results Info */}
                  {totalProducts > 0 && (
                    <div className="mt-5 mb-8 text-center font-hv-mono text-[12px] uppercase tracking-[0.04em] text-hv-soft px-0">
                      Showing {startIndex + 1}-{endIndex} of {totalProducts} products
                      {totalPages > 1 && (
                        <span className="ml-2 text-hv-orange">
                          • Page {currentPage} of {totalPages}
                        </span>
                      )}
                    </div>
                  )}
                </>
              )}

              {/* Show "No Products Found" only when query has completed and no products */}
              {displayProducts.length === 0 && !isLoading && !isFetching && productData && totalProducts === 0 && (
                <div className="text-center py-20 px-0">
                  <div className="max-w-md mx-auto">
                    <div className="w-20 h-20 bg-gradient-to-br from-hv-yellow to-hv-orange rounded-full flex items-center justify-center mx-auto mb-6">
                      <div className="w-10 h-10 border-2 border-white rounded-full flex items-center justify-center">
                        <span className="text-white font-bold text-lg">0</span>
                      </div>
                    </div>
                    <h3 className="text-2xl font-semibold text-hv-ink mb-4 tracking-[-0.01em]">No products found</h3>
                    <p className="text-hv-soft mb-6">
                      We couldn&apos;t find any products matching your criteria. Try adjusting your filters or browse other categories.
                    </p>
                    <Button
                      onClick={clearAllFilters}
                      className="h-12 rounded-xl bg-hv-ink px-8 font-semibold text-white shadow-none hover:bg-black"
                    >
                      Clear All Filters
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

// Separate components for better performance and to avoid re-mounting issues during drag
const PriceSlider = ({
  value,
  onValueCommit,
  min = 0,
  max = 20000
}: {
  value: [number, number],
  onValueCommit: (val: [number, number]) => void,
  min?: number,
  max?: number
}) => {
  const [localValue, setLocalValue] = useState<[number, number]>(value);

  // Sync local value when parent value changes (e.g. on clear all)
  useEffect(() => {
    setLocalValue(value);
  }, [value[0], value[1]]);

  return (
    <div className="px-2">
      <Slider
        min={min}
        max={max}
        step={1}
        value={localValue}
        onValueChange={(val) => setLocalValue(val as [number, number])}
        onValueCommit={(val) => onValueCommit(val as [number, number])}
        className="mb-4"
      />
      <div className="text-center text-sm font-medium text-hv-ink">
        ₹{localValue[0].toLocaleString('en-IN')} - ₹{localValue[1].toLocaleString('en-IN')}
      </div>
    </div>
  );
};

const FilterContent = ({
  isMobile = false,
  variantOptions,
  dynamicFilters,
  setDynamicFilters,
  tempFilters,
  setTempFilters,
  expandedSections,
  setExpandedSections,
  showAllOptions,
  setShowAllOptions,
  priceSliderRange,
  setPriceSliderRange
}: {
  isMobile?: boolean;
  variantOptions: any;
  dynamicFilters: any;
  setDynamicFilters: React.Dispatch<React.SetStateAction<any>>;
  tempFilters: any;
  setTempFilters: React.Dispatch<React.SetStateAction<any>>;
  expandedSections: Record<string, boolean>;
  setExpandedSections: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  showAllOptions: Record<string, boolean>;
  setShowAllOptions: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  priceSliderRange: [number, number];
  setPriceSliderRange: (val: [number, number]) => void;
}) => {
  const availableOptions = variantOptions?.options || {};
  const currentFilters = isMobile ? tempFilters : dynamicFilters;
  const setCurrentFilters = isMobile ? setTempFilters : setDynamicFilters;

  // Separate variant options from product properties
  const variantOptionKeys = ['Color Family', 'Finish', 'Look/Pattern', 'Size', 'Thickness'];
  const productPropertyKeys = ['Application', 'Application grade', 'Laminate Type', 'Product Type'];

  const variantOptionsFilters = Object.entries(availableOptions).filter(([key]) =>
    variantOptionKeys.includes(key)
  );

  const productPropertiesFilters = Object.entries(availableOptions).filter(([key]) =>
    productPropertyKeys.includes(key)
  );

  const handleOptionToggle = (optionName: string, value: string, checked: boolean, isProductProperty: boolean = false) => {
    // Pure update: copy the nested group too. Mutating prev's nested object made React's
    // double-run of this updater (Strict Mode) append the value twice.
    setCurrentFilters((prev: any) => {
      const filterType = isProductProperty ? 'productProperties' : 'variantOptions';
      const group = { ...(prev[filterType] || {}) };
      const current: string[] = group[optionName] || [];
      const next = checked
        ? Array.from(new Set([...current, value]))
        : current.filter((v) => v !== value);
      if (next.length > 0) group[optionName] = next;
      else delete group[optionName];
      return { ...prev, [filterType]: group };
    });
  };

  const renderFilterSection = (entries: [string, any][], isProductProperty: boolean = false) => {
    return entries.map(([optionName, values]: [string, any]) => {
      const optionKey = optionName.toLowerCase().replace(/\s+/g, '-');
      const isExpanded = expandedSections[optionKey] ?? true;
      const showAll = showAllOptions[optionKey] ?? false;
      const displayValues = showAll ? values : values.slice(0, 5);
      const hasMore = values.length > 5;
      const filterType = isProductProperty ? 'productProperties' : 'variantOptions';
      const selectedCount = currentFilters[filterType]?.[optionName]?.length || 0;

      return (
        <div key={optionName} className="border-b border-hv-line pb-4 last:border-b-0">
          <button
            onClick={() => setExpandedSections(prev => ({ ...prev, [optionKey]: !isExpanded }))}
            className="flex items-center justify-between w-full text-left py-2 rounded-lg transition-colors group"
          >
            <div className="flex items-center gap-2">
              <h4 className="font-hv-mono text-[12px] uppercase tracking-[0.06em] text-hv-ink">{optionName}</h4>
              <span className="font-hv-mono text-[11px] text-hv-soft">{values.length}</span>
              {selectedCount > 0 && (
                <span className="rounded-full bg-hv-orange px-2 py-0.5 text-[11px] font-semibold text-white">
                  {selectedCount}
                </span>
              )}
            </div>
            <svg className={`w-4 h-4 text-hv-soft group-hover:text-hv-ink transition-all ${isExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {isExpanded && (
            <div className="mt-2">
              {displayValues.map((item: any) => {
                const isChecked = currentFilters[filterType]?.[optionName]?.includes(item.value) || false;
                return (
                  <label
                    key={item.value}
                    className="flex items-center justify-between px-2 py-1.5 -mx-2 rounded-lg hover:bg-hv-paper transition-colors cursor-pointer group"
                  >
                    <div className="flex items-center space-x-3 flex-1">
                      <Checkbox
                        checked={isChecked}
                        onCheckedChange={(checked) => handleOptionToggle(optionName, item.value, checked as boolean, isProductProperty)}
                        className="rounded-[5px]"
                        // Inline so the ticked state never depends on a cached stylesheet having these colours
                        style={isChecked
                          ? { background: '#232224', borderColor: '#232224', color: '#FFFFFF' }
                          : { background: '#FFFFFF', borderColor: '#E4DFD6' }}
                      />
                      <span className="text-[14px] text-hv-ink">{item.value}</span>
                    </div>
                    <div className="font-hv-mono text-[11px] text-hv-soft">
                      {item.count}
                    </div>
                  </label>
                );
              })}
              {hasMore && (
                <button
                  onClick={() => setShowAllOptions(prev => ({ ...prev, [optionKey]: !showAll }))}
                  className="mt-2 text-[13px] font-semibold text-hv-orange transition-colors hover:text-hv-ink"
                >
                  {showAll ? '− Show less' : `+ Show ${values.length - 5} more`}
                </button>
              )}
            </div>
          )}
        </div>
      );
    });
  };

  return (
    <div className={`space-y-4 ${isMobile ? 'min-h-0' : ''}`}>
      {/* Price Range Slider */}
      <div className="border-b border-hv-line pb-4">
        <div className="flex items-center gap-2 mb-4">
          <h4 className="font-hv-mono text-[12px] uppercase tracking-[0.06em] text-hv-ink">Price</h4>
        </div>
        <PriceSlider value={priceSliderRange} onValueCommit={setPriceSliderRange} />
      </div>

      {variantOptionsFilters.length > 0 && renderFilterSection(variantOptionsFilters, false)}
      {productPropertiesFilters.length > 0 && renderFilterSection(productPropertiesFilters, true)}
    </div>
  );
};
